import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import useList from 'react-use/lib/useList';
import useDeepCompareEffect from 'react-use/lib/useDeepCompareEffect';

import styles from './PermissionsSelector.module.css';
import { Checkbox, Icon, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import {
  useQueryAllPermissions,
  useQueryPermissionByRole,
  useQueryPermissionByUser,
} from '../../../../query/user';

type Props = {
  userId?: number,
  role?: string,
};

function PermissionsSelector({ userId, role }: Props, ref) {
  const {
    isFetching: userPermissionLoading,
    data: userPermissionData,
  } = useQueryPermissionByUser(userId);
  const {
    isFetching: rolePermissionLoading,
    data: rolePermissionData,
  } = useQueryPermissionByRole(role);
  const {
    isFetching: allPermissionLoading,
    data: allPermissionData,
  } = useQueryAllPermissions();

  const currentUserPermissions = useSelector(
    state => state.auth.user.permissions,
  );

  const [selectedPermission, { set, filter, push }] = useList([]);

  useDeepCompareEffect(() => {
    set(userPermissionData);
  }, [userPermissionData, role]);

  useImperativeHandle(ref, () => ({
    getSelectedPermissions: () => selectedPermission,
  }));

  const isLoading =
    allPermissionLoading || userPermissionLoading || rolePermissionLoading;

  const renderLoading = useMemo(() => {
    return (
      <div className={styles.loadingOverlay}>
        <Icon style={{ color: '#fff' }} type="loading" />
        <p style={{ color: '#fff', marginLeft: 10, marginBottom: 0 }}>
          Loading Permissions...
        </p>
      </div>
    );
  }, []);

  return (
    <div className={styles.container}>
      <p style={{ marginBottom: 3 }}>Check Permission(/s)</p>
      <div className={styles.boxContainer}>
        {isLoading && renderLoading}
        <div className={styles.boxInner}>
          {allPermissionData.map(permission => {
            const isActive =
              (role === 'custom' && selectedPermission.includes(permission)) ||
              rolePermissionData.includes(permission);
            const insufficientUserPermission = !currentUserPermissions.includes(
              permission,
            );
            const isDisabled =
              rolePermissionData.includes(permission) ||
              role !== 'custom' ||
              insufficientUserPermission;
            let disabledTooltipMessage;
            if (insufficientUserPermission) {
              disabledTooltipMessage =
                'You cannot modify this permission because you do not have this permission';
            } else if (isDisabled) {
              disabledTooltipMessage =
                'Customize permissions is only allowed on custom role';
            }
            return (
              <Tooltip key={permission} title={disabledTooltipMessage}>
                <Checkbox
                  checked={isActive}
                  disabled={isDisabled}
                  style={{ marginLeft: 0, marginBottom: 3 }}
                  onChange={() => {
                    if (isActive) {
                      filter(e => e !== permission);
                    } else {
                      push(permission);
                    }
                  }}
                >
                  {permission}
                </Checkbox>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default forwardRef(PermissionsSelector);
